import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/Layout";
import { FormDialog } from "@/components/FormDialog";

const ROLES = [{ value: "employee", label: "Ansatt" }, { value: "admin", label: "Administrator" }];
const ROLE_LABEL = { owner: "Eier", admin: "Administrator", employee: "Ansatt" };

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => saas.employees.list().then(setList);
  useEffect(() => { load(); }, []);

  const fields = [
    { k: "name", label: "Navn" }, { k: "email", label: "E-post (innlogging)" },
    { k: "password", label: edit?.id ? "Nytt passord (valgfritt)" : "Passord (tomt = generert)" },
    { k: "role", label: "Rolle", type: "select", options: ROLES },
  ];
  const save = async () => {
    if (!edit.name || !edit.email) return toast.error("Navn og e-post må fylles ut");
    const body = { ...edit, password: edit.password || null };
    if (edit.id) {
      await saas.employees.update(edit.id, body);
      toast.success("Ansatt oppdatert");
    } else {
      const r = await saas.employees.create(body);
      toast.success(`Ansatt opprettet. Passord: ${r.temp_password}`, { duration: 15000 });
    }
    setEdit(null);
    load();
  };
  const remove = async (e) => { if (window.confirm(`Slette ${e.name}?`)) { await saas.employees.remove(e.id); load(); } };

  return (
    <div className="p-4 sm:p-8 max-w-4xl" data-testid="employees-page">
      <PageHeader title="Ansatte" subtitle="Ansatte logger inn og ser kun sine egne oppdrag">
        <Button data-testid="add-employee-button" onClick={() => setEdit({ name: "", email: "", password: "", role: "employee" })} className="gap-1.5"><Plus className="h-4 w-4" /> Ny ansatt</Button>
      </PageHeader>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        {list.map((e) => (
          <div key={e.id} data-testid={`employee-row-${e.id}`} className="flex items-center gap-3 p-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">{e.name?.[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{e.name}</div>
              <div className="text-xs text-muted-foreground truncate">{e.email}</div>
            </div>
            <span className="text-xs rounded-full bg-secondary px-2 py-0.5 font-semibold">{ROLE_LABEL[e.role]}</span>
            {e.id !== user.id && e.role !== "owner" && (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit({ ...e, password: "" })}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(e)}><Trash2 className="h-4 w-4" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
      {edit && <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Rediger ansatt" : "Ny ansatt"} testid="employee-dialog" fields={fields} value={edit} onChange={setEdit} onSubmit={save} />}
    </div>
  );
}
